import React from 'react'
import { Field, FieldArrayRenderProps, useFormikContext } from 'formik'
import { Button, Card, Columns, Element, Icon } from 'react-bulma-components'
import { isEqual } from 'lodash'
import InputField from '../../../../form/fields/input'
import SelectField from '../../../../form/fields/select'
import TextareaField from '../../../../form/fields/textarea'
import { RequestButton, RequestMessage } from '../../../../request-components/request-components'
import { AddResourceIcon, RemoveIcon } from '../../../../icons'
import {
  useCreatePost,
  useDeletePost,
  useUpdatePost,
} from '../../../../../queries/workers-requests'
// NOTE: `useUpdatePost` / `useCreatePost` are intentionally NOT called here: the
// mutation instances are owned by the top-level form and forwarded through props
// so the submit flow and the in-card feedback stay bound to the same instance.
import { useJobTitlesQuery } from '../../../../../queries/job-titles'
import { buildListAsInputOptions } from '../../../../../utils/forms'
import WorkPeriodTags from '../../../../work-periods/work-period-tags'
import { WorkersRequest } from '../../../../../api/workers-requests'
import { Employer } from '../../../../../api/organizations'
import config from '../../../../../../../backend/src/config'
import DesiredWorkersField from './desired-workers-field'
import { WorkersRequestCreateUpdateFormValues } from '../workers-request-form.types'
import { newPostTemplate } from '../utils/workers-request-form-get-initial-values'

/**
 * Recourse case reasons exposed as options for the SelectField.
 */
const allRecourseCasesReasons = config.workersRequests.recourseCases.reasons
const allRecourseCasesReasonsAsInputOptions = Object.entries(allRecourseCasesReasons).map(
  ([key, value]) => ({
    value: key,
    label: value,
  }),
)

/**
 * Props of the `<PostCard>` component.
 */
export interface PostCardProps {
  index: number
  post: WorkersRequestCreateUpdateFormValues['posts'][number]
  allPostsCount: number
  isLast: boolean
  arrayHelpers: FieldArrayRenderProps
  workersRequest?: WorkersRequest
  employer: Employer
  initialPost?: WorkersRequestCreateUpdateFormValues['posts'][number]
  isSubmitDisabled?: boolean
  // Forwarded from the top-level form (see note near the imports).
  updatePostMutation: ReturnType<typeof useUpdatePost>
  createPostMutation: ReturnType<typeof useCreatePost>
}

/**
 * Renders a single post (1 `<Card>`) inside the `<PostsSection>` FieldArray.
 *
 * Owns:
 * - the update/create/delete-post mutations (hooks are local to the card),
 * - the field-level diff used to enable/disable the "Update post" button,
 * - the "Add a post" button when the card is the last one in the list.
 */
const PostCard: React.FC<PostCardProps> = ({
  index,
  post,
  allPostsCount,
  isLast,
  arrayHelpers,
  workersRequest,
  employer,
  initialPost,
  isSubmitDisabled,
  updatePostMutation,
  createPostMutation,
}) => {
  const jobTitlesQuery = useJobTitlesQuery({})
  const deletePostMutation = useDeletePost()

  const { values } = useFormikContext<WorkersRequestCreateUpdateFormValues>()

  const isExistingPost = '_id' in post
  const isDraftPost = !('_id' in post)
  const existingPost = isExistingPost ? post : undefined
  const isNewWorkersRequest = workersRequest === undefined
  const isExistingWorkersRequest = !isNewWorkersRequest

  // Diff the current post against its initial values to know whether the
  // "Update post" button should be enabled. Replaces the old
  // `isPostValuesUpdated` bag that was mutated during render.
  const isPostUpdated = Boolean(initialPost && existingPost && !isEqual(initialPost, existingPost))

  // Predicate to determine whether the "Delete post" icon should appear:
  // - the post is persisted but has no attached missions (and there's more than one post);
  // - OR the post is a fresh draft on an already-created workers request;
  // - OR the post is a draft on a workers request being created (and not the sole one).
  const canBeDeleted =
    (existingPost && existingPost.missions.length === 0 && allPostsCount > 1) ||
    (isDraftPost && !isNewWorkersRequest) ||
    (isNewWorkersRequest && allPostsCount > 1)

  const jobTitleDoc = jobTitlesQuery.data?.find(
    jT => jT._id.toString() === values.posts[index]?.jobTitle,
  )

  return (
    <>
      <Card mb={5}>
        <Card.Header p={3}>
          <Card.Header.Title>
            <Element renderAs="span" textColor="primary" textSize={5}>
              {post.count && post.jobTitle
                ? post.count +
                  ' x ' +
                  jobTitlesQuery.data?.find(jT => post.jobTitle === jT._id.toString())?.name
                : undefined}
            </Element>
            {post.recourseCase.reason ? (
              // @ts-expect-error -- reason is a string keyof `allRecourseCasesReasons`.
              <>&nbsp; | {allRecourseCasesReasons[post.recourseCase.reason]}</>
            ) : (
              ''
            )}
            {post.recourseCase.justification ? <>&nbsp; | {post.recourseCase.justification}</> : ''}
          </Card.Header.Title>
          <Card.Header.Icon>
            {canBeDeleted && (
              <div className="field">
                <Button
                  color="danger"
                  size={'small'}
                  onClick={() => {
                    if (isExistingWorkersRequest && isExistingPost) {
                      // Existing workers request + existing post: call the API to delete.
                      deletePostMutation.mutate({
                        workersRequestId: workersRequest._id,
                        postId: post._id,
                      })
                    } else {
                      // Draft: only remove from Formik state.
                      arrayHelpers.remove(index)
                    }
                  }}
                  type="button"
                >
                  <Icon>
                    <RemoveIcon />
                  </Icon>
                  <Element renderAs="span">Supprimer ce Poste</Element>
                </Button>
              </div>
            )}
          </Card.Header.Icon>
        </Card.Header>
        <Card.Content pt={1}>
          <Columns>
            {isDraftPost && (
              <Columns.Column size={2}>
                <Field
                  label={'Nombre'}
                  name={`posts.${index}.count`}
                  component={InputField}
                  min={1}
                  type="number"
                  required
                  fullwidth="true"
                />
              </Columns.Column>
            )}
            <Columns.Column>
              <Field
                label="Métier"
                name={`posts.${index}.jobTitle`}
                component={SelectField}
                required
                fullwidth="true"
                options={buildListAsInputOptions(jobTitlesQuery.data || [])}
                selectDefaultChooseOptionLabel={'Métier...'}
                disabled={workersRequest && !workersRequest.__actions.requestedWorkersCanBeUpdated}
              />
              {jobTitleDoc?.complementaryInformations ? (
                <Card>
                  <Card.Header>
                    <Card.Header.Title>Infos transmises</Card.Header.Title>
                  </Card.Header>
                  <Card.Content>{jobTitleDoc.complementaryInformations}</Card.Content>
                </Card>
              ) : undefined}
            </Columns.Column>
            <Columns.Column>
              <Field
                label={'Cas de Recours'}
                name={`posts.${index}.recourseCase.reason`}
                component={SelectField}
                required
                fullwidth="true"
                options={allRecourseCasesReasonsAsInputOptions}
                selectDefaultChooseOptionLabel="Choisir un Cas de Recours"
                disabled={workersRequest && !workersRequest.__actions.requestedWorkersCanBeUpdated}
              />
            </Columns.Column>
            <Columns.Column>
              <Field
                label={'Justification'}
                component={InputField}
                name={`posts.${index}.recourseCase.justification`}
                required
                fullwidth="true"
                disabled={workersRequest && !workersRequest.__actions.requestedWorkersCanBeUpdated}
              />
            </Columns.Column>
          </Columns>
          <Columns>
            <Columns.Column>
              <PostTagsField index={index} workersRequest={workersRequest} />
            </Columns.Column>
          </Columns>
          <Columns>
            <Columns.Column>
              <Field
                label={<Element>Description du Poste pour l'Agence d'Intérim</Element>}
                component={TextareaField}
                name={`posts.${index}.description`}
                fullwidth="true"
                disabled={workersRequest && !workersRequest.__actions.requestedWorkersCanBeUpdated}
              />
            </Columns.Column>
          </Columns>
          <Columns>
            <Columns.Column>
              <DesiredWorkersField
                fieldName={`posts.${index}.desiredWorkers`}
                fieldValue={values.posts[index]?.desiredWorkers ?? []}
                employer={employer}
              />
            </Columns.Column>
          </Columns>
        </Card.Content>
        {isExistingPost && (
          <Card.Footer>
            <Card.Footer.Item>
              <Element>
                <Element>
                  <RequestMessage
                    mutation={updatePostMutation}
                    displayOn={{ success: 'Le poste a été mis à jour' }}
                  />
                </Element>
                <RequestButton
                  mutation={updatePostMutation}
                  color="primary"
                  type="submit"
                  disabled={isSubmitDisabled || !isPostUpdated}
                  data-test={`submit-workers-request-post-update-${index}-form`}
                >
                  {'Mettre à jour le Poste'}
                </RequestButton>
              </Element>
            </Card.Footer.Item>
          </Card.Footer>
        )}
        {isExistingWorkersRequest && isDraftPost && (
          <Card.Footer>
            <Card.Footer.Item>
              <Element>
                <Element>
                  <RequestMessage
                    mutation={createPostMutation}
                    displayOn={{ success: 'Le poste a été créé' }}
                  />
                </Element>
                <RequestButton
                  mutation={createPostMutation}
                  color="primary"
                  type="submit"
                  disabled={isSubmitDisabled}
                  data-test={`submit-workers-request-post-create-${index}-form`}
                >
                  {'Créer le Poste'}
                </RequestButton>
              </Element>
            </Card.Footer.Item>
          </Card.Footer>
        )}
      </Card>
      {isLast && (
        <Card mb={6}>
          <Card.Content>
            <Button
              textAlign="right"
              color="primary"
              outlined
              onClick={() => {
                arrayHelpers.push(structuredClone(newPostTemplate))
              }}
              type="button"
            >
              <Icon>
                <AddResourceIcon />
              </Icon>
              <Element renderAs="span">Ajouter un Poste à Pourvoir</Element>
            </Button>
          </Card.Content>
        </Card>
      )}
    </>
  )
}

export default PostCard

/**
 * Small wrapper around `WorkPeriodTags` used inside PostCard to keep the tag-related
 * logic co-located. Reads / writes `posts.${index}.tags` through the Formik context.
 */
const PostTagsField: React.FC<{
  index: number
  workersRequest?: WorkersRequest
}> = ({ index, workersRequest }) => {
  const { values, setFieldValue } = useFormikContext<WorkersRequestCreateUpdateFormValues>()

  return (
    <Element mb={3}>
      <Element mb={2}>
        <Element renderAs="label" className="label">
          Catégories
        </Element>
      </Element>
      <WorkPeriodTags
        tags={values.posts[index]?.tags || []}
        formProps={{
          onSubmit: params => {
            setFieldValue(`posts.${index}.tags`, params.tags)
          },
          onCancel: () => {},
          dateRange: false,
        }}
        onTagDelete={tagId => {
          setFieldValue(
            `posts.${index}.tags`,
            values.posts[index]?.tags?.filter(tag => tag._id !== tagId),
          )
        }}
        isEditable={!workersRequest || workersRequest.__actions.requestedWorkersCanBeUpdated}
      />
    </Element>
  )
}
